import { mockSuccess } from './shared';
import type { MockRouteEntry } from '../mock-api.types';

type Draft = {
    id: string;
    status: string;
    subject: string;
    to: string[];
    body_html: string;
    vetting_mode?: string;
};

let VETTING_PREFS = { default_mode: 'self', default_reviewer_user_id: null as number | null };


const CALENDARS = [
    { id: 'cal-mailbox', title: 'Mailbox', color: 'bg-teal-500', visible: true },
    { id: 'cal-campaigns', title: 'Campaign sends', color: 'bg-indigo-500', visible: true },
    { id: 'cal-meetings', title: 'Meetings', color: 'bg-amber-500', visible: true },
];

const INBOX = [
    {
        id: 'in-1',
        from: 'customer@outbox.demo',
        subject: 'Re: June newsletter',
        preview: 'Thanks for the update…',
        body_html: '<p>Thanks for the update. Can we schedule a call?</p>',
        received_at: '2026-06-04T08:14:23.000Z',
        read: false,
        mailbox_id: 'mb-1',
    },
    {
        id: 'in-2',
        from: 'noreply@outbox.demo',
        subject: 'Delivery report',
        preview: '3 messages delivered successfully.',
        body_html: '<p>3 messages delivered successfully.</p>',
        received_at: '2026-06-03T10:14:23.000Z',
        read: true,
        mailbox_id: 'mb-2',
    },
];

let CALENDAR_EVENTS = [
    {
        id: 'ev-1',
        calendar_id: 'cal-meetings',
        title: 'Client check-in',
        description: 'Quarterly review with account team.',
        location: 'Teams',
        start: '2026-06-04T10:00:00.000Z',
        end: '2026-06-04T11:00:00.000Z',
        all_day: false,
        event_type: 'meeting',
        attendees: ['sales@outbox.demo', 'customer@outbox.demo'],
    },
    {
        id: 'ev-2',
        calendar_id: 'cal-campaigns',
        title: 'Newsletter send window',
        description: 'Scheduled campaign blast.',
        start: '2026-06-06T09:00:00.000Z',
        end: '2026-06-06T10:00:00.000Z',
        all_day: false,
        event_type: 'scheduled_send',
    },
    {
        id: 'ev-3',
        calendar_id: 'cal-mailbox',
        title: 'Mailbox maintenance',
        start: '2026-06-09T00:00:00.000Z',
        end: '2026-06-10T00:00:00.000Z',
        all_day: true,
        event_type: 'reminder',
    },
];

function filterEvents(url: string): typeof CALENDAR_EVENTS {
    const u = new URL(url, 'http://local');
    const start = u.searchParams.get('start');
    const end = u.searchParams.get('end');
    if (!start || !end) {
        return CALENDAR_EVENTS;
    }
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    return CALENDAR_EVENTS.filter((ev) => {
        const evs = new Date(ev.start).getTime();
        const eve = new Date(ev.end).getTime();
        return evs < e && eve > s;
    });
}

function findEvent(url: string) {
    const m = url.match(/\/calendar\/events\/([^/?]+)/i);
    if (!m) return undefined;
    return CALENDAR_EVENTS.find((x) => x.id === m[1]);
}

const MAILBOXES = [
    { id: 'mb-1', address: 'sales@outbox.demo', domain: 'outbox.demo', plan: 'standard', quota_mb: 5120, status: 'active' },
    { id: 'mb-2', address: 'noreply@outbox.demo', domain: 'outbox.demo', plan: 'transactional', quota_mb: 1024, status: 'active' },
];

const DRAFTS: Draft[] = [
    {
        id: 'mail-1',
        status: 'draft',
        subject: 'June newsletter draft',
        to: ['team@yopmail.com'],
        body_html: '<p>Hello from UI-only mock.</p>',
        vetting_mode: 'self',
    },
    {
        id: 'mail-2',
        status: 'pending_review',
        subject: 'Product launch (delegate vet)',
        to: ['launch@yopmail.com'],
        body_html: '<p>Pending reviewer approval.</p>',
        vetting_mode: 'delegate',
    },
    {
        id: 'mail-3',
        status: 'sent',
        subject: 'Welcome sequence (sent)',
        to: ['welcome@yopmail.com'],
        body_html: '<p>Delivered via mock SMTP.</p>',
        vetting_mode: 'self',
    },
];


function findInbox(url: string) {
    const m = url.match(/\/inbox\/([^/?]+)/i);
    if (!m) return undefined;
    return INBOX.find((x) => x.id === m[1]);
}

function findDraft(url: string): Draft | undefined {
    const m = url.match(/\/drafts\/([^/]+)/i);
    if (!m) {
        return undefined;
    }
    return DRAFTS.find((d) => d.id === m[1]);
}


export const MAILING_MOCK_HANDLERS: MockRouteEntry[] = [
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/calendar\/calendars/i,
        handler: () => mockSuccess(CALENDARS),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/calendar\/events/i,
        handler: (req) => mockSuccess({ data: filterEvents(req.url) }),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/calendar\/events\/[^/?]+/i,
        handler: (req) => mockSuccess(findEvent(req.url) ?? CALENDAR_EVENTS[0]),
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/calendar\/events(\?|$)/i,
        handler: (req) => {
            const body = (req.body || {}) as Record<string, unknown>;
            const next = {
                id: `ev-${CALENDAR_EVENTS.length + 1}`,
                calendar_id: String(body['calendar_id'] || 'cal-meetings'),
                title: String(body['title'] || 'Event'),
                description: body['description'] as string | undefined,
                location: body['location'] as string | undefined,
                start: String(body['start'] || new Date().toISOString()),
                end: String(body['end'] || new Date().toISOString()),
                all_day: Boolean(body['all_day']),
                event_type: (body['event_type'] as string) || 'meeting',
                attendees: body['attendees'] as string[] | undefined,
            };
            CALENDAR_EVENTS.push(next);
            return mockSuccess(next);
        },
    },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/calendar\/events\/[^/?]+/i,
        handler: (req) => {
            const ev = findEvent(req.url);
            if (ev) Object.assign(ev, req.body as object);
            return mockSuccess(ev ?? CALENDAR_EVENTS[0]);
        },
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/inbox\/[^/?]+/i,
        handler: (req) => mockSuccess(findInbox(req.url) ?? INBOX[0]),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/inbox(\?|$)/i,
        handler: () => mockSuccess({ data: INBOX }),
    },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/inbox\/[^/?]+/i,
        handler: (req) => {
            const m = req.url.match(/\/inbox\/([^/?]+)/i);
            const row = INBOX.find((x) => x.id === m?.[1]);
            if (row) row.read = true;
            return mockSuccess(row ?? INBOX[0]);
        },
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/mailboxes/i,
        handler: () => mockSuccess({ data: MAILBOXES }),
    },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/vetting-preferences/i,
        handler: (req) => {
            VETTING_PREFS = { ...VETTING_PREFS, ...(req.body as object) };
            return mockSuccess(VETTING_PREFS);
        },
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts(\?|$)/i,
        handler: () => mockSuccess({ data: DRAFTS }),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/vetting-preferences/i,
        handler: () => mockSuccess(VETTING_PREFS),
    },
    {
        method: 'GET',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts\/[^/]+/i,
        handler: (req) => {
            const d = findDraft(req.url);
            return mockSuccess(d ?? DRAFTS[0]);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts\/[^/]+\/send/i,
        handler: (req) => {
            const d = findDraft(req.url);
            if (d) {
                d.status = 'sent';
            }
            return mockSuccess(d ?? DRAFTS[0], 'Sent (mock)');
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts\/[^/]+\/(submit|approve|reject|ai-draft)/i,
        handler: (req) => {
            const d = findDraft(req.url);
            if (!d) {
                return mockSuccess(DRAFTS[0]);
            }
            if (req.url.includes('/submit')) {
                const mode = (req.body as { vetting_mode?: string })?.vetting_mode;
                d.status = mode === 'delegate' ? 'pending_review' : 'approved';
            } else if (req.url.includes('/approve')) {
                d.status = 'approved';
            } else if (req.url.includes('/reject')) {
                d.status = 'rejected';
            } else if (req.url.includes('/ai-draft')) {
                d.body_html = '<p>AI revised copy (mock).</p>';
            }
            return mockSuccess(d);
        },
    },
    {
        method: 'POST',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts(\?|$)/i,
        handler: (req) => {
            const body = (req.body || {}) as Partial<Draft>;
            const next: Draft = {
                id: `mail-${DRAFTS.length + 1}`,
                status: 'draft',
                subject: body.subject || 'Untitled',
                to: Array.isArray(body.to) ? body.to : [],
                body_html: body.body_html || '',
                vetting_mode: body.vetting_mode || 'self',
            };
            DRAFTS.push(next);
            return mockSuccess(next);
        },
    },
    {
        method: 'PATCH',
        pattern: /\/api\/[^/]+\/[^/]+\/mailing\/drafts\/[^/]+/i,
        handler: (req) => {
            const d = findDraft(req.url);
            if (d) {
                Object.assign(d, req.body as Partial<Draft>);
            }
            return mockSuccess(d ?? DRAFTS[0]);
        },
    },
];
